import { useCallback, useEffect, useRef, useState } from 'react';
import {
  IDENTITY, MAX_SCALE, MIN_SCALE, STEP, clampView, focalOf, panView, pinchView, transformOf, wheelFactor, zoomAround,
} from '../utils/panZoom';

/*
 * Pan and pinch-zoom for a picture, and for the drawing canvases that sit on
 * one (2026-10-15). The arithmetic is utils/panZoom.js; this turns pointers,
 * the wheel and keys into it.
 *
 *   - Two fingers pinch and move at once; lifting one hands over to a
 *     one-finger move instead of stopping.
 *   - One pointer moves the picture once it is magnified ('pan-when-zoomed'),
 *     or is left to the canvas beneath ('ignore', a drawing tool): a second
 *     finger then cancels what the first began (`onGestureStart`) and its
 *     moves never reach the canvas, so a pinch draws nothing. A middle-button
 *     drag, Space held, or `panTool` move it with one pointer anyway.
 *   - The wheel zooms where the pointer is: always, or only with Ctrl/Cmd
 *     ('ctrl', where the page must still scroll; a trackpad's pinch arrives
 *     as a Ctrl+wheel). A mouse wheel's notch is one step, a trackpad's
 *     stream in proportion.
 *   - A double tap (or double click) zooms in, or back out; off on canvases,
 *     where a double click finishes a line.
 *   - +/-/0 zoom and reset, Shift+arrows move: on the document, but never
 *     for a key meant for a field or another dialog ('document'), or only
 *     while the frame has focus ('focus').
 *
 * The pointer handlers listen in the capture phase, on the frame, so nothing
 * inside it can hide a finger (a mark that stops its pointerdown).
 */
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_PX = 30;
const TAP_SLOP_PX = 8;
const KEY_PAN_PX = 40;
export const DOUBLE_TAP_SCALE = 2.5;

const isField = el => !!el && (['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName) || el.isContentEditable);

export default function usePanZoom({
  min = MIN_SCALE, max = MAX_SCALE, resetKey, singlePointer = 'pan-when-zoomed', panTool = false,
  wheel = 'always', doubleTap = true, keyboard = 'document', inline = false, onGestureStart, measure,
} = {}) {
  const frameRef = useRef(null);
  const [view, setViewState] = useState(IDENTITY);
  const [gesturing, setGesturing] = useState(false);
  const viewRef = useRef(IDENTITY);
  const pointers = useRef(new Map());
  const gesture = useRef(null);
  const lastTap = useRef(null);
  const toggledAt = useRef(0);
  const space = useRef(false);
  const latest = useRef({});
  latest.current = { min, max, singlePointer, panTool, wheel, doubleTap, keyboard, onGestureStart, measure };

  const frameBox = () => frameRef.current?.getBoundingClientRect() || { left: 0, top: 0, width: 0, height: 0 };
  const sizes = () => {
    const rect = frameBox();
    const frame = { width: rect.width, height: rect.height };
    const content = latest.current.measure?.() || frame;
    return { rect, frame, content };
  };

  const setView = useCallback((next) => {
    viewRef.current = next;
    setViewState(next);
  }, []);

  const zoomTo = useCallback((scale, focal) => {
    const { frame, content } = sizes();
    setView(zoomAround(viewRef.current, scale, focal, frame, content, latest.current.min, latest.current.max));
  }, [setView]); // eslint-disable-line react-hooks/exhaustive-deps
  const zoomBy = useCallback((factor, focal) => zoomTo(viewRef.current.scale * factor, focal), [zoomTo]);
  const zoomIn = useCallback(() => zoomBy(STEP), [zoomBy]);
  const zoomOut = useCallback(() => zoomBy(1 / STEP), [zoomBy]);
  const reset = useCallback(() => setView(IDENTITY), [setView]);
  const panBy = useCallback((dx, dy) => {
    const { frame, content } = sizes();
    setView(panView(viewRef.current, dx, dy, frame, content, latest.current.min, latest.current.max));
  }, [setView]); // eslint-disable-line react-hooks/exhaustive-deps

  // A new picture opens whole.
  useEffect(() => { reset(); }, [resetKey, reset]);

  const toggleZoom = useCallback((focal) => {
    if (viewRef.current.scale > 1) reset();
    else zoomTo(DOUBLE_TAP_SCALE, focal);
  }, [reset, zoomTo]);

  // The wheel, natively: React's listener is passive, and a passive one
  // cannot keep the page from scrolling behind the picture.
  useEffect(() => {
    const el = frameRef.current;
    if (!el) return undefined;
    const onWheel = (e) => {
      if (latest.current.wheel === 'ctrl' && !(e.ctrlKey || e.metaKey)) return;
      if (latest.current.wheel === false) return;
      e.preventDefault();
      zoomBy(wheelFactor(e), focalOf(e.clientX, e.clientY, frameBox()));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [zoomBy]);

  const onKey = useCallback((e) => {
    if (e.defaultPrevented || isField(e.target)) return;
    if (e.key === ' ' || e.code === 'Space') { space.current = e.type === 'keydown'; return; }
    if (e.type !== 'keydown') return;
    let handled = true;
    if (e.key === '+' || e.key === '=') zoomBy(STEP);
    else if (e.key === '-' || e.key === '_') zoomBy(1 / STEP);
    else if (e.key === '0') reset();
    else if (e.shiftKey && e.key === 'ArrowLeft') panBy(KEY_PAN_PX, 0);
    else if (e.shiftKey && e.key === 'ArrowRight') panBy(-KEY_PAN_PX, 0);
    else if (e.shiftKey && e.key === 'ArrowUp') panBy(0, KEY_PAN_PX);
    else if (e.shiftKey && e.key === 'ArrowDown') panBy(0, -KEY_PAN_PX);
    else handled = false;
    if (handled && e.shiftKey) e.preventDefault();
  }, [zoomBy, reset, panBy]);

  // Keys on the document, but never one meant for another dialog: with the
  // photo annotator open over the viewer, its keys are the annotator's.
  useEffect(() => {
    if (keyboard !== 'document') return undefined;
    const handler = (e) => {
      const own = frameRef.current?.closest?.('[role="dialog"]') || null;
      const at = e.target?.closest?.('[role="dialog"]') || null;
      if (at && at !== own) return;
      onKey(e);
    };
    document.addEventListener('keydown', handler);
    document.addEventListener('keyup', handler);
    return () => {
      document.removeEventListener('keydown', handler);
      document.removeEventListener('keyup', handler);
    };
  }, [keyboard, onKey]);

  const pointOf = (e, rect) => ({ ...focalOf(e.clientX, e.clientY, rect), clientX: e.clientX, clientY: e.clientY });

  const capture = (e) => {
    try { frameRef.current?.setPointerCapture?.(e.pointerId); } catch (_) { /* jsdom, or released */ }
  };

  const startPan = (e, explicit) => {
    gesture.current = { type: 'pan', last: { x: e.clientX, y: e.clientY }, explicit, moved: 0 };
  };

  const onPointerDown = (e) => {
    const { rect } = sizes();
    const o = latest.current;
    // A middle-button drag always moves the picture.
    if (e.pointerType === 'mouse' && e.button === 1) {
      e.preventDefault();
      e.stopPropagation();
      startPan(e, true);
      pointers.current.set(e.pointerId, pointOf(e, rect));
      capture(e);
      return;
    }
    pointers.current.set(e.pointerId, pointOf(e, rect));
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.values()];
      gesture.current = { type: 'pinch', start: { view: viewRef.current, a, b } };
      setGesturing(true);
      o.onGestureStart?.();
      e.stopPropagation();
      capture(e);
      return;
    }
    if (pointers.current.size > 2) {
      e.stopPropagation();
      return;
    }
    if (o.panTool || space.current) {
      e.stopPropagation();
      startPan(e, true);
      capture(e);
      return;
    }
    if (o.singlePointer === 'pan-when-zoomed') {
      startPan(e, false);
      capture(e);
    } else {
      gesture.current = null;
    }
  };

  const onPointerMove = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    const { rect, frame, content } = sizes();
    pointers.current.set(e.pointerId, pointOf(e, rect));
    const g = gesture.current;
    if (!g) return;
    const o = latest.current;
    if (g.type === 'pinch' && pointers.current.size >= 2) {
      const [a, b] = [...pointers.current.values()];
      setView(pinchView(g.start, a, b, frame, content, o.min, o.max));
      e.stopPropagation();
      e.preventDefault();
    } else if (g.type === 'pan') {
      const dx = e.clientX - g.last.x;
      const dy = e.clientY - g.last.y;
      g.last = { x: e.clientX, y: e.clientY };
      g.moved += Math.abs(dx) + Math.abs(dy);
      if (viewRef.current.scale > 1 || g.explicit) {
        setView(panView(viewRef.current, dx, dy, frame, content, o.min, o.max));
        if (g.moved > TAP_SLOP_PX) setGesturing(true);
      }
      if (g.explicit || g.afterPinch) e.stopPropagation();
    }
  };

  const tapped = (e) => {
    if (!latest.current.doubleTap || e.pointerType !== 'touch') return;
    const now = Date.now();
    const at = { x: e.clientX, y: e.clientY };
    const before = lastTap.current;
    if (before && now - before.t < DOUBLE_TAP_MS && Math.hypot(at.x - before.x, at.y - before.y) < DOUBLE_TAP_PX) {
      lastTap.current = null;
      toggledAt.current = now;
      toggleZoom(focalOf(e.clientX, e.clientY, frameBox()));
    } else {
      lastTap.current = { t: now, ...at };
    }
  };

  const onPointerUp = (e) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.delete(e.pointerId);
    const g = gesture.current;
    if (g?.type === 'pinch') {
      // The canvas never sees a pinch's fingers lift: it began nothing.
      e.stopPropagation();
      if (pointers.current.size === 1) {
        const [rest] = [...pointers.current.values()];
        gesture.current = { type: 'pan', last: { x: rest.clientX, y: rest.clientY }, explicit: false,
          afterPinch: true, moved: TAP_SLOP_PX + 1 };
      } else {
        gesture.current = null;
        setGesturing(false);
      }
      return;
    }
    if (g?.type === 'pan') {
      if (g.explicit || g.afterPinch) e.stopPropagation();
      else if (g.moved <= TAP_SLOP_PX && e.type === 'pointerup') tapped(e);
    } else if (e.type === 'pointerup') {
      tapped(e);
    }
    if (pointers.current.size === 0) {
      gesture.current = null;
      setGesturing(false);
    }
  };

  const onDoubleClick = (e) => {
    if (!latest.current.doubleTap) return;
    // A touch double tap may also arrive as a dblclick: once is enough.
    if (Date.now() - toggledAt.current < 500) return;
    toggleZoom(focalOf(e.clientX, e.clientY, frameBox()));
  };

  const zoomed = view.scale > 1;
  let touchAction = 'none';
  if (singlePointer === 'pan-when-zoomed' && !zoomed && inline) touchAction = 'pan-y';

  const frameProps = {
    ref: frameRef,
    onPointerDownCapture: onPointerDown,
    onPointerMoveCapture: onPointerMove,
    onPointerUpCapture: onPointerUp,
    onPointerCancelCapture: onPointerUp,
    onDoubleClick,
    ...(keyboard === 'focus' ? { onKeyDown: onKey, onKeyUp: onKey } : {}),
    style: { touchAction },
  };

  return {
    frameRef,
    view,
    zoomed,
    gesturing,
    zoomIn,
    zoomOut,
    zoomTo,
    reset,
    panBy,
    setView,
    frameProps,
    contentStyle: { transform: transformOf(view), transformOrigin: 'center center' },
    clamp: () => {
      const { frame, content } = sizes();
      setView(clampView(viewRef.current, frame, content, min, max));
    },
  };
}
